import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../../contexts/TenantContext';
import { useAuth } from '../../../contexts/AuthContext';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Divider,
  Avatar,
  IconButton,
  InputAdornment,
  TextField,
  useTheme,
  alpha,
  Tooltip,
  Container,
} from '@mui/material';
import { 
  Search as SearchIcon, 
  Notifications as NotificationsIcon,
  TrendingUp as TrendingUpIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Visibility as VisibilityIcon,
  AccountBalance as AccountBalanceIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Payment as PaymentIcon,
  DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import financialRequestService from '../../../services/financialRequestService';

// SLA configuration for each status (in hours)
const SLA_CONFIG = {
  'Requested': 24, // 24 hours to move from Requested to Review
  'Review': 48,    // 48 hours to move from Review to Approved/Declined
  'Approved': 72,  // 72 hours to disburse funds after approval
  'Disbursed': 72  // 72 hours for member to confirm receipt
};

// Helper function to calculate SLA status
const getSlaStatus = (status, statusDate) => {
  if (!status || !statusDate || !SLA_CONFIG[status]) return 'none';
  
  const now = new Date();
  const startDate = new Date(statusDate);
  const elapsedHours = (now - startDate) / (1000 * 60 * 60);
  const slaLimit = SLA_CONFIG[status];
  
  // Calculate SLA threshold percentages
  const warningThreshold = 0.7; // 70% of SLA time used = amber
  const criticalThreshold = 1.0; // 100% of SLA time used = red
  
  if (elapsedHours >= slaLimit * criticalThreshold) {
    return 'red'; // Exceeded SLA
  } else if (elapsedHours >= slaLimit * warningThreshold) {
    return 'amber'; // Approaching SLA limit
  } else {
    return 'green'; // Within SLA
  }
};

// Helper to get color from SLA status
const getSlaStatusColor = (theme, status) => {
  switch (status) {
    case 'red':
      return theme.palette.error.main;
    case 'amber':
      return theme.palette.warning.main;
    case 'green':
      return theme.palette.success.main;
    default:
      return theme.palette.text.secondary;
  }
};

// Helper function to get status color
const getStatusColor = (status) => {
  switch (status) {
    case 'Requested':
      return 'info';
    case 'Review':
      return 'warning';
    case 'Approved':
      return 'success';
    case 'Declined':
      return 'error';
    case 'MemberRejected':
      return 'error';
    case 'Disbursed':
      return 'success';
    case 'Completed':
      return 'success';
    default:
      return 'default';
  }
};

// Helper function to format dates
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Get the count of SLA exceeded requests
const countExceededSlaRequests = (requests) => {
  if (!requests || !Array.isArray(requests)) return 0;
  
  return requests.filter(request => {
    const statusDate = request.statusDate || request.lastUpdated || request.requestDate;
    const slaStatus = getSlaStatus(request.status, statusDate);
    return slaStatus === 'red'; // Only count "red" status which means SLA exceeded
  }).length;
};

const StatusIcon = ({ status }) => {
  switch(status) {
    case 'Requested':
      return <AccessTimeIcon fontSize="small" />;
    case 'Review':
      return <FilterListIcon fontSize="small" />;
    case 'Approved':
      return <CheckCircleIcon fontSize="small" />;
    case 'Declined':
    case 'MemberRejected':
      return <CancelIcon fontSize="small" />;
    case 'Disbursed':
      return <PaymentIcon fontSize="small" />;
    case 'Completed':
      return <CheckCircleIcon fontSize="small" />;
    default:
      return null;
  }
};

const AdminFinancialRequestsPage = () => {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { user } = useAuth();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({
    total: 0,
    requested: 0,
    inReview: 0,
    approved: 0,
    declined: 0,
    memberRejected: 0,
    disbursed: 0,
    completed: 0
  });
  
  // Pagination and filtering
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  // Additional state for SLA filtering
  const [slaFilter, setSlaFilter] = useState(null);

  // Add a function to handle filtering by SLA status
  const handleFilterBySla = (slaStatus) => {
    // Store the SLA filter preference in session storage
    sessionStorage.setItem('slaFilter', slaStatus);
    
    // Reset to page 1 whenever we apply a new filter
    setPage(1);
    
    // Trigger a refresh to apply the filter
    setRefreshing(true);
  };

  // Function to clear SLA filter
  const clearSlaFilter = () => {
    sessionStorage.removeItem('slaFilter');
    setSlaFilter(null);
    setRefreshing(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Check if there's an SLA filter stored in session storage
        const storedSlaFilter = sessionStorage.getItem('slaFilter');
        if (storedSlaFilter) {
          setSlaFilter(storedSlaFilter);
        }

        if (!tenant || !tenant._id) {
          console.warn('No tenant information available. Using any available tenant.');
        } else {
          console.log(`Using tenant: ${tenant._id}`);
          localStorage.setItem('tenantId', tenant._id);
        }
        
        if (user && user.id) {
          console.log(`Using user: ${user.id}, role: ${user.role}`);
          localStorage.setItem('userId', user.id);
        }

        // Get current tenant ID from context or localStorage
        const currentTenantId = tenant?._id || localStorage.getItem('tenantId');
        
        if (!currentTenantId) {
          console.error('No tenant ID available. This may cause issues with data filtering.');
        }

        console.log('Fetching financial request counts...');
        const countsResponse = await financialRequestService.getRequestCounts(currentTenantId);
        console.log('Counts response:', countsResponse);
        
        const countsData = {
          total: countsResponse?.total || 0,
          requested: countsResponse?.requested || 0,
          inReview: countsResponse?.inReview || 0,
          approved: countsResponse?.approved || 0,
          declined: countsResponse?.declined || 0,
          memberRejected: countsResponse?.memberRejected || 0,
          disbursed: countsResponse?.disbursed || 0,
          completed: countsResponse?.completed || 0
        };
        
        setCounts(countsData);
        
        console.log(`Fetching financial requests with page=${page}, limit=${limit}, status=${statusFilter !== 'all' ? statusFilter : 'all'}, tenantId=${currentTenantId}`);
        const response = await financialRequestService.getAllRequests(
          page, 
          limit, 
          statusFilter !== 'all' ? statusFilter : '',
          currentTenantId
        );
        
        console.log('Financial requests response:', response);
        let filteredRequests = response.data || [];

        // Apply SLA filter if it exists
        if (slaFilter) {
          filteredRequests = filteredRequests.filter(request => {
            const statusDate = request.statusDate || request.lastUpdated || request.requestDate;
            const requestSlaStatus = getSlaStatus(request.status, statusDate);
            return requestSlaStatus === slaFilter;
          });
        }
        
        setRequests(filteredRequests);
        setTotalPages(response.totalPages || 1);
      } catch (err) {
        console.error('Error fetching financial requests:', err);
        setError(err.message || 'Error loading financial requests. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page, limit, statusFilter, tenant, user, refreshing, slaFilter]);

  const handleViewRequest = (requestId) => {
    navigate(`/admin/financial-requests/${requestId}`);
  };

  const handleViewMember = (memberId) => {
    if (memberId) {
      navigate(`/admin/members/${memberId}`);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(1);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 500);
  };

  const statusDisplay = {
    'Requested': 'Requested',
    'Review': 'In Review',
    'Approved': 'Approved',
    'Declined': 'Declined',
    'MemberRejected': 'Rejected by Member',
    'Disbursed': 'Funds Disbursed',
    'Completed': 'Completed'
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3, px: { xs: 2, md: 3 } }}>
      <Box sx={{ 
        backgroundColor: alpha(theme.palette.primary.light, 0.04),
        minHeight: '100vh',
      }}>
        <Card 
          elevation={0} 
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            p: 2,
            backgroundImage: `linear-gradient(to right, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
            color: 'white'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <AccountBalanceIcon sx={{ mr: 1 }} />
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
              Financial Assistance Management
            </Typography>
          </Box>
          <Typography variant="body2">
            Review and manage member financial assistance requests
          </Typography>
        </Card>

        <Paper 
          elevation={0} 
          sx={{ 
            p: 3,
            borderRadius: 2,
            boxShadow: theme.shadows[2],
            backgroundColor: '#ffffff',
            mb: 4
          }}
        >
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>Request Statistics</Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={6} sm={6} md={4} lg={2}>
              <Card 
                sx={{ 
                  p: 1,
                  height: '100%',
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Requests
                    </Typography>
                    <Avatar sx={{ bgcolor: theme.palette.grey[200], width: 40, height: 40 }}>
                      <TrendingUpIcon color="primary" fontSize="small" />
                    </Avatar>
                  </Box>
                  <Typography variant="h4" sx={{ mt: 2, fontWeight: 'bold' }}>
                    {counts.total}
                  </Typography>
                  <Box sx={{ 
                    mt: 1, 
                    p: 0.5, 
                    bgcolor: alpha(theme.palette.success.main, 0.1), 
                    borderRadius: 1,
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}>
                    <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center' }}>
                      <ArrowUpwardIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                      Active Management
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Exceeded SLA card - NEW */}
            <Grid item xs={6} sm={6} md={4} lg={2}>
              <Card 
                sx={{ 
                  p: 1,
                  height: '100%',
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  background: !loading ? alpha(theme.palette.error.main, 0.05) : 'inherit',
                  border: !loading && countExceededSlaRequests(requests) > 0 ? `1px solid ${alpha(theme.palette.error.main, 0.3)}` : 'none',
                  position: 'relative',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                  },
                  // Highlight when SLA filter is active
                  ...(slaFilter === 'red' && {
                    boxShadow: `0 0 0 2px ${theme.palette.error.main}`,
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.error.main,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      zIndex: 1
                    }
                  })
                }}
                onClick={() => handleFilterBySla('red')}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Exceeded SLA
                    </Typography>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), width: 40, height: 40 }}>
                      <NotificationsIcon color="error" fontSize="small" />
                    </Avatar>
                  </Box>
                  <Typography variant="h4" color="error.main" sx={{ mt: 2, fontWeight: 'bold' }}>
                    {!loading ? countExceededSlaRequests(requests) : <CircularProgress size={24} />}
                  </Typography>
                  <Box sx={{ 
                    mt: 1, 
                    p: 0.5, 
                    bgcolor: alpha(theme.palette.error.main, 0.1), 
                    borderRadius: 1,
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}>
                    <Typography variant="caption" color="error">
                      {slaFilter === 'red' ? 'Filtered View Active' : 'Require Immediate Action'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={6} sm={6} md={4} lg={2}>
              <Card 
                sx={{ 
                  p: 1,
                  height: '100%',
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      New Requests
                    </Typography>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), width: 40, height: 40 }}>
                      <AccessTimeIcon color="info" fontSize="small" />
                    </Avatar>
                  </Box>
                  <Typography variant="h4" color="info.main" sx={{ mt: 2, fontWeight: 'bold' }}>
                    {counts.requested}
                  </Typography>
                  <Box sx={{ 
                    mt: 1, 
                    p: 0.5, 
                    bgcolor: alpha(theme.palette.info.main, 0.1), 
                    borderRadius: 1,
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}>
                    <Typography variant="caption" color="info.main">
                      Awaiting Review
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={6} md={4} lg={2}>
              <Card 
                sx={{ 
                  p: 1,
                  height: '100%',
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      In Review
                    </Typography>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), width: 40, height: 40 }}>
                      <FilterListIcon color="warning" fontSize="small" />
                    </Avatar>
                  </Box>
                  <Typography variant="h4" color="warning.main" sx={{ mt: 2, fontWeight: 'bold' }}>
                    {counts.inReview}
                  </Typography>
                  <Box sx={{ 
                    mt: 1, 
                    p: 0.5, 
                    bgcolor: alpha(theme.palette.warning.main, 0.1), 
                    borderRadius: 1,
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}>
                    <Typography variant="caption" color="warning.main">
                      Under Evaluation
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={6} md={4} lg={2}>
              <Card 
                sx={{ 
                  p: 1,
                  height: '100%',
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Approved
                    </Typography>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), width: 40, height: 40 }}>
                      <CheckCircleIcon color="success" fontSize="small" />
                    </Avatar>
                  </Box>
                  <Typography variant="h4" color="success.main" sx={{ mt: 2, fontWeight: 'bold' }}>
                    {counts.approved}
                  </Typography>
                  <Box sx={{ 
                    mt: 1, 
                    p: 0.5, 
                    bgcolor: alpha(theme.palette.success.main, 0.1), 
                    borderRadius: 1,
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}>
                    <Typography variant="caption" color="success.main">
                      Ready for Disbursement
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={6} md={4} lg={2}>
              <Card 
                sx={{ 
                  p: 1,
                  height: '100%',
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Disbursed
                    </Typography>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), width: 40, height: 40 }}>
                      <PaymentIcon color="primary" fontSize="small" />
                    </Avatar>
                  </Box>
                  <Typography variant="h4" color="primary.main" sx={{ mt: 2, fontWeight: 'bold' }}>
                    {counts.disbursed}
                  </Typography>
                  <Box sx={{ 
                    mt: 1, 
                    p: 0.5, 
                    bgcolor: alpha(theme.palette.primary.main, 0.1), 
                    borderRadius: 1,
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}>
                    <Typography variant="caption" color="primary.main">
                      Funds Sent
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={6} md={4} lg={2}>
              <Card 
                sx={{ 
                  p: 1,
                  height: '100%',
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Completed
                    </Typography>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.grey[800], 0.1), width: 40, height: 40 }}>
                      <DoneAllIcon color="success" fontSize="small" />
                    </Avatar>
                  </Box>
                  <Typography variant="h4" color="text.primary" sx={{ mt: 2, fontWeight: 'bold' }}>
                    {counts.completed}
                  </Typography>
                  <Box sx={{ 
                    mt: 1, 
                    p: 0.5, 
                    bgcolor: alpha(theme.palette.grey[500], 0.1), 
                    borderRadius: 1,
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}>
                    <Typography variant="caption" color="text.secondary">
                      Closed Requests
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        <Card 
          sx={{ 
            mb: 3, 
            p: 2, 
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Request Management
            </Typography>
            <Tooltip title="Refresh data">
              <IconButton onClick={handleRefresh} color="primary" sx={{ 
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Divider sx={{ mb: 2 }} />
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            gap: 2, 
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'center' },
            mb: 3 
          }}>
            <TextField
              placeholder="Search by member name or reason..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                sx: { borderRadius: 1 }
              }}
              sx={{ flexGrow: 1, maxWidth: { md: 300 } }}
            />
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {slaFilter === 'red' && (
                <Chip
                  label="SLA Exceeded Filter Active"
                  color="error"
                  onDelete={clearSlaFilter}
                  size="small"
                  sx={{ 
                    fontWeight: 'medium',
                    '& .MuiChip-deleteIcon': {
                      color: 'error.light'
                    }
                  }}
                />
              )}
              
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="status-filter-label">Filter by Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  id="status-filter"
                  value={statusFilter}
                  label="Filter by Status"
                  onChange={handleStatusFilterChange}
                  sx={{ borderRadius: 1 }}
                >
                  <MenuItem value="all">All Requests</MenuItem>
                  <MenuItem value="Requested">New Requests</MenuItem>
                  <MenuItem value="Review">In Review</MenuItem>
                  <MenuItem value="Approved">Approved</MenuItem>
                  <MenuItem value="Declined">Declined</MenuItem>
                  <MenuItem value="MemberRejected">Rejected by Member</MenuItem>
                  <MenuItem value="Disbursed">Funds Disbursed</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 1 }}>
              {error}
            </Alert>
          ) : requests.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 1 }}>
              <Typography variant="body1" color="text.secondary">
                No financial assistance requests found.
              </Typography>
            </Paper>
          ) : (
            <>
              <TableContainer sx={{ 
                boxShadow: 'none', 
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                mb: 2
              }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Request Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Member</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Reason</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Last Updated</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>SLA Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requests.map((request) => (
                      <TableRow 
                        key={request._id} 
                        hover 
                        sx={{ 
                          transition: 'background-color 0.2s',
                          '&:hover': { backgroundColor: alpha(theme.palette.primary.light, 0.05) },
                          cursor: 'pointer',
                        }}
                        onClick={() => handleViewRequest(request._id)}
                      >
                        <TableCell>{formatDate(request.requestDate)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ 
                              width: 30, 
                              height: 30, 
                              bgcolor: theme.palette.primary.light,
                              mr: 1,
                              fontSize: '0.875rem'
                            }}>
                              {request.member?.userId?.firstName?.charAt(0) || 'U'}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {request.member?.userId?.firstName} {request.member?.userId?.lastName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {request.reason}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {request.amount > 0 
                            ? <Typography sx={{ fontFamily: 'monospace' }}>${request.amount.toFixed(2)}</Typography> 
                            : <Typography variant="body2" color="text.secondary">Not specified</Typography>}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            icon={<StatusIcon status={request.status} />}
                            label={statusDisplay[request.status] || request.status} 
                            color={getStatusColor(request.status)} 
                            size="small"
                            sx={{ fontWeight: 500, minWidth: 120 }}
                          />
                        </TableCell>
                        <TableCell>{formatDate(request.lastUpdated)}</TableCell>
                        <TableCell>
                          {(() => {
                            // Determine which date to use for SLA calculation
                            const statusDate = request.statusDate || request.lastUpdated || request.requestDate;
                            const slaStatus = getSlaStatus(request.status, statusDate);
                            
                            return (
                              <Chip
                                size="small"
                                label={(() => {
                                  switch(slaStatus) {
                                    case 'green': return 'Within SLA';
                                    case 'amber': return 'Approaching Limit';
                                    case 'red': return 'SLA Exceeded';
                                    default: return 'No SLA';
                                  }
                                })()}
                                sx={{
                                  bgcolor: alpha(getSlaStatusColor(theme, slaStatus), 0.1),
                                  color: getSlaStatusColor(theme, slaStatus),
                                  fontWeight: 'medium',
                                  minWidth: 110
                                }}
                              />
                            );
                          })()}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View details">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewRequest(request._id);
                              }}
                              sx={{ 
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                                }
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <Pagination 
                  count={totalPages} 
                  page={page} 
                  onChange={handlePageChange} 
                  color="primary"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                  sx={{
                    '& .MuiPaginationItem-root': {
                      borderRadius: 1,
                    }
                  }}
                />
              </Box>
            </>
          )}
        </Card>
      </Box>
    </Container>
  );
};

export default AdminFinancialRequestsPage;